import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { MAX_SCHEDULE_MILESTONES } from "@common/constants/domains/accounting/schedules";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    applyScheduleTemplateBodySchema,
    applyScheduleTemplateBulkBodySchema,
    applyScheduleTemplateBulkResponseSchema,
    applyScheduleTemplateResponseSchema,
    unapplyScheduleApplicationsBodySchema,
    unapplyScheduleApplicationsResponseSchema,
} from "@common/schemas/domains/accounting/scheduleApplication.schema";
import {
    listScheduleApplicationsResponseSchema,
    listWorkspaceScheduleApplicationsQuerySchema,
    listWorkspaceScheduleApplicationsResponseSchema,
} from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import {
    listScheduleCandidatesQuerySchema,
    listScheduleCandidatesResponseSchema,
} from "@common/schemas/domains/accounting/scheduleCandidates.schema";
import {
    deleteScheduleApplicationsBulkBodySchema,
    deleteScheduleApplicationsBulkResponseSchema,
} from "@common/schemas/domains/accounting/scheduleDeleteBulk.schema";
import {
    scheduleLifecycleBulkBodySchema,
    scheduleLifecycleBulkResponseSchema,
} from "@common/schemas/domains/accounting/scheduleLifecycleBulk.schema";
import {
    listScheduleLinkedSourcesQuerySchema,
    listScheduleLinkedSourcesResponseSchema,
    scheduleBulkLinkedSourceConflictErrorSchema,
} from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import {
    scheduleBillingActionResponseSchema,
    scheduleMarkBilledBodySchema,
    scheduleMarkBilledBulkBodySchema,
    scheduleMarkBilledBulkErrorSchema,
    scheduleMarkBilledBulkResponseSchema,
    scheduleMarkUnbilledBodySchema,
} from "@common/schemas/domains/accounting/scheduleBilling.schema";
import {
    scheduleConflictResponseSchema,
    scheduleDeleteResponseSchema,
    scheduleLifecycleActionBodySchema,
    scheduleLifecycleCancelBodySchema,
    scheduleLifecycleActionResponseSchema,
    scheduleLifecycleChangeBodySchema,
    scheduleLifecycleDeleteBodySchema,
    scheduleLifecycleProposalQuerySchema,
    scheduleLifecycleProposalSchema,
} from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import {
    confirmAllRecognitionPeriodsBodySchema,
    confirmRecognitionPeriodsBodySchema,
    confirmRecognitionPeriodsResponseSchema,
    confirmScheduleDraftsBodySchema,
    confirmScheduleDraftsResponseSchema,
    listRecognitionReviewPeriodsResponseSchema,
    scheduleDraftApplicationIdsBodySchema,
    scheduleDraftApplicationIdsResponseSchema,
    scheduleRecognitionReviewQuerySchema,
    scheduleSuspiciousTransactionIdsBodySchema,
    scheduleSuspiciousTransactionIdsResponseSchema,
} from "@common/schemas/domains/accounting/scheduleReview.schema";
import {
    recognizeScheduleMilestoneBodySchema,
    recognizeScheduleMilestonesBulkBodySchema,
    recognizeScheduleMilestonesBulkResponseSchema,
    recognizeScheduleMilestoneResponseSchema,
    scheduleMilestoneAlreadyPostedErrorSchema,
} from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import {
    createScheduleTemplateBodySchema,
    listScheduleTemplatesQuerySchema,
    previewScheduleRecognitionBodySchema,
    previewScheduleRecognitionResponseSchema,
    scheduleTemplateListItemSchema,
    scheduleTemplateSchema,
    updateScheduleTemplateBodySchema,
} from "@common/schemas/domains/accounting/scheduleTemplate.schema";

const c = initContract();

const workspacePathParams = z.object({
    workspaceId: z.string().uuid(),
});

const workspaceResourcePathParams = z.object({
    workspaceId: z.string().uuid(),
    id: z.string().uuid(),
});

const scheduleMilestonePathParams = workspaceResourcePathParams.extend({
    sortOrder: z.coerce.number().int().positive().max(MAX_SCHEDULE_MILESTONES),
});

export const scheduleContract = c.router(
    {
        listTemplates: {
            method: "GET",
            path: "/:workspaceId/templates",
            pathParams: workspacePathParams,
            query: listScheduleTemplatesQuerySchema,
            responses: {
                200: z.object({
                    templates: z.array(scheduleTemplateListItemSchema),
                }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getTemplate: {
            method: "GET",
            path: "/:workspaceId/templates/:id",
            pathParams: workspaceResourcePathParams,
            responses: {
                200: z.object({
                    template: scheduleTemplateSchema,
                }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createTemplate: {
            method: "POST",
            path: "/:workspaceId/templates",
            pathParams: workspacePathParams,
            body: createScheduleTemplateBodySchema,
            responses: {
                201: z.object({
                    template: scheduleTemplateSchema,
                }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateTemplate: {
            method: "PUT",
            path: "/:workspaceId/templates/:id",
            pathParams: workspaceResourcePathParams,
            body: updateScheduleTemplateBodySchema,
            responses: {
                200: z.object({
                    template: scheduleTemplateSchema,
                }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        listWorkspaceApplications: {
            method: "GET",
            path: "/:workspaceId/applications",
            pathParams: workspacePathParams,
            query: listWorkspaceScheduleApplicationsQuerySchema,
            responses: {
                200: listWorkspaceScheduleApplicationsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        listApplications: {
            method: "GET",
            path: "/:workspaceId/templates/:id/applications",
            pathParams: workspaceResourcePathParams,
            responses: {
                200: listScheduleApplicationsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        applyTemplate: {
            method: "POST",
            path: "/:workspaceId/templates/:id/apply",
            pathParams: workspaceResourcePathParams,
            body: applyScheduleTemplateBodySchema,
            responses: {
                201: applyScheduleTemplateResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: scheduleConflictResponseSchema,
            },
        },
        applyTemplateBulk: {
            method: "POST",
            path: "/:workspaceId/templates/:id/apply-bulk",
            pathParams: workspaceResourcePathParams,
            body: applyScheduleTemplateBulkBodySchema,
            responses: {
                201: applyScheduleTemplateBulkResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: z.union([
                    scheduleBulkLinkedSourceConflictErrorSchema,
                    ErrorMessageSchema,
                ]),
            },
        },
        unapplyApplications: {
            method: "POST",
            path: "/:workspaceId/applications/unapply",
            pathParams: workspacePathParams,
            body: unapplyScheduleApplicationsBodySchema,
            responses: {
                200: unapplyScheduleApplicationsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        archiveTemplate: {
            method: "POST",
            path: "/:workspaceId/templates/:id/archive",
            pathParams: workspaceResourcePathParams,
            body: z.object({}),
            responses: {
                200: z.object({ template: scheduleTemplateSchema }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        restoreTemplate: {
            method: "POST",
            path: "/:workspaceId/templates/:id/restore",
            pathParams: workspaceResourcePathParams,
            body: z.object({}),
            responses: {
                200: z.object({ template: scheduleTemplateSchema }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        deleteTemplate: {
            method: "DELETE",
            path: "/:workspaceId/templates/:id",
            pathParams: workspaceResourcePathParams,
            responses: {
                200: z.object({ id: z.string().uuid() }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        deleteApplication: {
            method: "POST",
            path: "/:workspaceId/applications/:id/delete",
            pathParams: workspaceResourcePathParams,
            body: scheduleLifecycleDeleteBodySchema,
            responses: {
                200: scheduleDeleteResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: scheduleConflictResponseSchema,
            },
        },
        deleteApplicationsBulk: {
            method: "POST",
            path: "/:workspaceId/applications/delete-bulk",
            pathParams: workspacePathParams,
            body: deleteScheduleApplicationsBulkBodySchema,
            responses: {
                200: deleteScheduleApplicationsBulkResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        lifecycleBulk: {
            method: "POST",
            path: "/:workspaceId/applications/lifecycle-bulk",
            pathParams: workspacePathParams,
            body: scheduleLifecycleBulkBodySchema,
            responses: {
                200: scheduleLifecycleBulkResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getLifecycleProposal: {
            method: "GET",
            path: "/:workspaceId/applications/:id/lifecycle-proposal",
            pathParams: workspaceResourcePathParams,
            query: scheduleLifecycleProposalQuerySchema,
            responses: {
                200: scheduleLifecycleProposalSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        cancelApplication: {
            method: "POST",
            path: "/:workspaceId/applications/:id/cancel",
            pathParams: workspaceResourcePathParams,
            body: scheduleLifecycleCancelBodySchema,
            responses: {
                200: scheduleLifecycleActionResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: scheduleConflictResponseSchema,
            },
        },
        pauseApplication: {
            method: "POST",
            path: "/:workspaceId/applications/:id/pause",
            pathParams: workspaceResourcePathParams,
            body: scheduleLifecycleActionBodySchema,
            responses: {
                200: scheduleLifecycleActionResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: scheduleConflictResponseSchema,
            },
        },
        resumeApplication: {
            method: "POST",
            path: "/:workspaceId/applications/:id/resume",
            pathParams: workspaceResourcePathParams,
            body: scheduleLifecycleActionBodySchema,
            responses: {
                200: scheduleLifecycleActionResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: scheduleConflictResponseSchema,
            },
        },
        markApplicationBilled: {
            method: "POST",
            path: "/:workspaceId/applications/:id/mark-billed",
            pathParams: workspaceResourcePathParams,
            body: scheduleMarkBilledBodySchema,
            responses: {
                200: scheduleBillingActionResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: scheduleConflictResponseSchema,
            },
        },
        markApplicationUnbilled: {
            method: "POST",
            path: "/:workspaceId/applications/:id/mark-unbilled",
            pathParams: workspaceResourcePathParams,
            body: scheduleMarkUnbilledBodySchema,
            responses: {
                200: scheduleBillingActionResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: scheduleConflictResponseSchema,
            },
        },
        markApplicationsBilledBulk: {
            method: "POST",
            path: "/:workspaceId/applications/mark-billed-bulk",
            pathParams: workspacePathParams,
            body: scheduleMarkBilledBulkBodySchema,
            responses: {
                200: scheduleMarkBilledBulkResponseSchema,
                400: z.union([
                    scheduleMarkBilledBulkErrorSchema,
                    ErrorMessageSchema,
                ]),
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        changeApplication: {
            method: "POST",
            path: "/:workspaceId/applications/:id/change",
            pathParams: workspaceResourcePathParams,
            body: scheduleLifecycleChangeBodySchema,
            responses: {
                200: scheduleLifecycleActionResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: scheduleConflictResponseSchema,
            },
        },
        recognizeMilestone: {
            method: "POST",
            path: "/:workspaceId/applications/:id/milestones/:sortOrder/recognize",
            pathParams: scheduleMilestonePathParams,
            body: recognizeScheduleMilestoneBodySchema,
            responses: {
                200: recognizeScheduleMilestoneResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: z.union([
                    scheduleMilestoneAlreadyPostedErrorSchema,
                    scheduleConflictResponseSchema,
                ]),
            },
        },
        recognizeMilestonesBulk: {
            method: "POST",
            path: "/:workspaceId/applications/milestones/recognize-bulk",
            pathParams: workspacePathParams,
            body: recognizeScheduleMilestonesBulkBodySchema,
            responses: {
                200: recognizeScheduleMilestonesBulkResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        previewRecognition: {
            method: "POST",
            path: "/:workspaceId/templates/preview",
            pathParams: workspacePathParams,
            body: previewScheduleRecognitionBodySchema,
            responses: {
                200: previewScheduleRecognitionResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        listRecognitionReviewPeriods: {
            method: "GET",
            path: "/:workspaceId/review/recognition",
            pathParams: workspacePathParams,
            query: scheduleRecognitionReviewQuerySchema,
            responses: {
                200: listRecognitionReviewPeriodsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        confirmRecognitionPeriods: {
            method: "POST",
            path: "/:workspaceId/review/recognition/confirm",
            pathParams: workspacePathParams,
            body: confirmRecognitionPeriodsBodySchema,
            responses: {
                200: confirmRecognitionPeriodsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        confirmAllRecognitionPeriods: {
            method: "POST",
            path: "/:workspaceId/review/recognition/confirm-all",
            pathParams: workspacePathParams,
            body: confirmAllRecognitionPeriodsBodySchema,
            responses: {
                200: confirmRecognitionPeriodsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        listScheduleCandidates: {
            method: "GET",
            path: "/:workspaceId/review/candidates",
            pathParams: workspacePathParams,
            query: listScheduleCandidatesQuerySchema,
            responses: {
                200: listScheduleCandidatesResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        listLinkedSources: {
            method: "GET",
            path: "/:workspaceId/review/linked-sources",
            pathParams: workspacePathParams,
            query: listScheduleLinkedSourcesQuerySchema,
            responses: {
                200: listScheduleLinkedSourcesResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        dismissSuspiciousTransactions: {
            method: "POST",
            path: "/:workspaceId/review/suspicious/dismiss",
            pathParams: workspacePathParams,
            body: scheduleSuspiciousTransactionIdsBodySchema,
            responses: {
                200: scheduleSuspiciousTransactionIdsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        restoreSuspiciousTransactions: {
            method: "POST",
            path: "/:workspaceId/review/suspicious/restore",
            pathParams: workspacePathParams,
            body: scheduleSuspiciousTransactionIdsBodySchema,
            responses: {
                200: scheduleSuspiciousTransactionIdsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        confirmScheduleDrafts: {
            method: "POST",
            path: "/:workspaceId/review/drafts/confirm",
            pathParams: workspacePathParams,
            body: confirmScheduleDraftsBodySchema,
            responses: {
                200: confirmScheduleDraftsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        dismissScheduleDrafts: {
            method: "POST",
            path: "/:workspaceId/review/drafts/dismiss",
            pathParams: workspacePathParams,
            body: scheduleDraftApplicationIdsBodySchema,
            responses: {
                200: scheduleDraftApplicationIdsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        restoreScheduleDrafts: {
            method: "POST",
            path: "/:workspaceId/review/drafts/restore",
            pathParams: workspacePathParams,
            body: scheduleDraftApplicationIdsBodySchema,
            responses: {
                200: scheduleDraftApplicationIdsResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    { pathPrefix: "/schedules" },
);
